import React, { createContext, ReactNode, useCallback, useState } from 'react'

type SidenavExpansionContextActions = {
  /** Check if a nav item is expanded */
  isExpanded: (itemId: string) => boolean
  /** Toggle expansion state of a nav item */
  toggleExpanded: (itemId: string) => void
  /** Set expansion state of a nav item */
  setExpanded: (itemId: string, expanded: boolean) => void
}

type SidenavExpansionContextState = {
  expandedItems: Set<string>
}

type SidenavExpansionContextValue = SidenavExpansionContextState &
  SidenavExpansionContextActions

const stubSidenavExpansionContextActions: SidenavExpansionContextActions = {
  isExpanded: () => {
    throw Error('SidenavItemsExpandedContext not initialized')
  },
  toggleExpanded: () => {
    throw Error('SidenavItemsExpandedContext not initialized')
  },
  setExpanded: () => {
    throw Error('SidenavItemsExpandedContext not initialized')
  },
}

const defaultSidenavExpansionContextState: SidenavExpansionContextState = {
  expandedItems: new Set<string>(),
}

export const SidenavItemsExpandedContext =
  createContext<SidenavExpansionContextValue>({
    ...defaultSidenavExpansionContextState,
    ...stubSidenavExpansionContextActions,
  })

type SidenavExpansionProviderProps = {
  children: ReactNode
}

export function SidenavExpansionProvider(
  props: SidenavExpansionProviderProps,
): React.ReactElement {
  const { children } = props
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const isExpanded = useCallback(
    (itemId: string): boolean => {
      return expandedItems.has(itemId)
    },
    [expandedItems],
  )

  const setExpanded = useCallback((itemId: string, expanded: boolean): void => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (expanded) {
        newSet.add(itemId)
      } else {
        newSet.delete(itemId)
      }
      return newSet
    })
  }, [])

  const toggleExpanded = useCallback(
    (itemId: string): void => {
      const currentlyExpanded = expandedItems.has(itemId)
      setExpanded(itemId, !currentlyExpanded)
    },
    [expandedItems, setExpanded],
  )

  return (
    <SidenavItemsExpandedContext.Provider
      value={{
        expandedItems,
        isExpanded,
        toggleExpanded,
        setExpanded,
      }}
    >
      {children}
    </SidenavItemsExpandedContext.Provider>
  )
}
